// Account API - passwordless user accounts (passkeys + magic link).
// Browser sessions live in an HttpOnly cookie. Authorization remains a
// backend-only compatibility path for scripts and one-time legacy migration.
import { apiFetch, ensureOk } from './apiError';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '/api';

const TOKEN_KEY = 'bpb_account_token';

export function getLegacyAccountToken() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function clearLegacyAccountToken() {
  if (typeof window !== 'undefined') localStorage.removeItem(TOKEN_KEY);
}

async function request(path, options = {}) {
  const response = await apiFetch(`${API_BASE_URL}/account${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  await ensureOk(response, `Request failed (${response.status})`);
  try {
    return await response.json();
  } catch {
    return null; // geen JSON body
  }
}

export const migrateLegacyAccountSession = (token) =>
  request('/session', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: '{}',
  });

export const logoutAccount = () => request('/logout', { method: 'POST', body: '{}' });

// --- Magic link ---
export const requestMagicLink = (email) =>
  request('/magic-link', { method: 'POST', body: JSON.stringify({ email }) });

export const verifyMagicLink = (token) =>
  request('/magic-link/verify', { method: 'POST', body: JSON.stringify({ token }) });

// --- Passkeys ---
export const passkeyRegisterOptions = () =>
  request('/passkeys/register-options', { method: 'POST', body: '{}' });

export const passkeyRegister = (response, deviceName) =>
  request('/passkeys/register', { method: 'POST', body: JSON.stringify({ response, deviceName }) });

export const passkeyLoginOptions = () =>
  request('/passkeys/login-options', { method: 'POST', body: '{}' });

export const passkeyLogin = (key, response) =>
  request('/passkeys/login', { method: 'POST', body: JSON.stringify({ key, response }) });

export const listPasskeys = () => request('/passkeys');

export const deletePasskey = (id) => request(`/passkeys/${id}`, { method: 'DELETE' });

// --- Profiel ---
export const fetchMe = () => request('/me');

export const updateProfile = (displayName) =>
  request('/me', { method: 'PATCH', body: JSON.stringify({ displayName }) });

export const deleteAccount = () => request('/me', { method: 'DELETE' });

// --- Matching-sessie claimen ---
export const claimSession = (sessionId) =>
  request('/claim-session', { method: 'POST', body: JSON.stringify({ session_id: sessionId }) });

// --- Favorieten ---
export const fetchFavorites = () => request('/favorites');
export const addFavorite = (setId) => request(`/favorites/${setId}`, { method: 'PUT' });
export const removeFavorite = (setId) => request(`/favorites/${setId}`, { method: 'DELETE' });

// --- Collectie ---
export const fetchCollection = () => request('/collection');
export const addToCollection = (setId) => request(`/collection/${setId}`, { method: 'PUT' });
export const removeFromCollection = (setId) => request(`/collection/${setId}`, { method: 'DELETE' });

// --- Check-progress sync ---
export const fetchAllProgress = () => request('/progress');
export const fetchProgress = (setId) => request(`/progress/${setId}`);
export const saveProgress = (setId, { progress, checked, total, updatedAt }) =>
  request(`/progress/${setId}`, {
    method: 'PUT',
    body: JSON.stringify({ progress, checked, total, updatedAt }),
  });
export const deleteProgress = (setId) => request(`/progress/${setId}`, { method: 'DELETE' });

// --- Badges ---
export const fetchBadges = () => request('/badges');
